import {useMemo} from "react";
import {SectionDiff} from "../../utils/branchDiff";
import {useIntegrator} from "../../integrator.context";
import {classifyGlobalResolve, collectCurrentKnownIds} from "../../utils/resolveDifference";
import {ResolveGlobalFieldCommand} from "../../commands/resolveGlobalField.command";
import {FieldChange} from "../../utils/recordDiff";
import ActionButton from "./actionButton";

// Renders a globals-flavoured section ("Game Settings", "Styles"): fields of the
// model diffed as one pseudo-record. Each changed field gets a Resolve button
// that pulls Other's value into Current via ResolveGlobalFieldCommand.
function formatScalar(v: unknown): string {
    if (v === undefined) return 'missing';
    if (v === null) return 'null';
    if (typeof v === 'string') return v === '' ? '""' : v;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

function GlobalsSectionView(props: { section: SectionDiff }) {
    const {state, executeCommand} = useIntegrator();
    const fieldChanges: FieldChange[] = props.section.differs[0]?.fieldChanges ?? [];

    const knownIds = useMemo(() => collectCurrentKnownIds(state), [state]);

    return (
        <div className={'layout vertical'} style={{gap: 2, marginTop: 4}}>
            {fieldChanges.map((fc, i) => {
                const path = fc.displayPath ?? fc.path.join('.');
                const cls = classifyGlobalResolve(fc, knownIds, knownIds);
                const resolveButton = (
                    <div style={{alignSelf: 'flex-start'}}>
                        <ActionButton
                            variant={'pull'}
                            small
                            label={'Pull'}
                            disabled={!cls.resolvable}
                            title={cls.resolvable ? 'Resolve: pull this from Other into Current' : (cls.reason ?? '')}
                            onClick={() => executeCommand(new ResolveGlobalFieldCommand(cls.actions))}/>
                    </div>
                );

                if (fc.kind === 'set') {
                    const added = fc.displayAdded ?? fc.added;
                    const removed = fc.displayRemoved ?? fc.removed;
                    return (
                        <div key={i} className={'layout horizontal'}
                             style={{borderLeft: 'solid 2px var(--overlay-color)', paddingLeft: 8, gap: 8, alignItems: 'flex-start'}}>
                            <div className={'layout vertical flex-grow'}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>{path}</p>
                                {added && added.length > 0 && <p style={{color: '#66cc66'}}>+ {added.join(', ')}</p>}
                                {removed && removed.length > 0 && <p style={{color: '#ff6666'}}>- {removed.join(', ')}</p>}
                            </div>
                            {resolveButton}
                        </div>
                    );
                }

                return (
                    <div key={i} className={'layout horizontal gap'}
                         style={{borderLeft: 'solid 2px var(--overlay-color)', paddingLeft: 8, gap: 8}}>
                        <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12, minWidth: 200}}>{path}</p>
                        <p style={{color: '#ff6666'}}>{formatScalar(fc.displayCurrent ?? fc.current)}</p>
                        <span className={'fa-solid fa-arrow-right'} style={{color: 'rgba(255,255,255,0.66)', fontSize: 11, alignSelf: 'center'}}/>
                        <p style={{color: '#66cc66', flexGrow: 1}}>{formatScalar(fc.displayTarget ?? fc.target)}</p>
                        {resolveButton}
                    </div>
                );
            })}
        </div>
    );
}

export default GlobalsSectionView;
